import React, { memo, useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, Tab, Tabs } from "@mui/material";
import { PlusIcon } from "src/Icons";
import { getAvailableStudioTemplates } from "src/utils/helpers";
import { InfoString } from "src/utils/infoString";

import { SubmitButton } from "../Button/SubmitButton";
import { SkillSelector } from "../Selector/CustomEmployeeSkillsSelector";

const CustomTabs = styled(Tabs)({
  border: "1px solid #D6D6DB",
  padding: "7px 16px",
  borderRadius: "8px",
  width: "656px",
  "& .MuiButtonBase-root": {
    fontSize: "16px",
    fontWeight: 500,
    color: "black",
    borderRadius: "6px",
  },
  "& .MuiTabs-flexContainer": {
    justifyContent: "space-between",
  },
  "& .Mui-selected": {
    color: "white !important",
    backgroundColor: "black",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent",
  },
  "& .MuiTab-root": {
    padding: 0,
    minHeight: "32px",
    minWidth: 0,
  },
});

interface EmployeeTabsWrapperProps {
  handleAddServiceButton: (id: any) => void;
  handleExistingServicesChange: (newWorkplace:any) => void;
  loadedExistingServices:any[];
  studiosSelected: any;
  newServicesList: any[];
}

const EmployeeSkillService = memo(({
  handleAddServiceButton,
  handleExistingServicesChange,
  loadedExistingServices,
  studiosSelected,
}: EmployeeTabsWrapperProps) => {

  const [activeStep, setActiveStep] = useState(0);

  const handleChangeStep = (step: any) => {
    setActiveStep(step);
  };

  const handlePressNext = useCallback(() => {
    handleChangeStep(activeStep + 1);
  }, [activeStep]);

  const handlePressPrev = useCallback(() => {
    handleChangeStep(activeStep - 1);
  }, [activeStep]);

  useEffect(() => {
    if (activeStep >= studiosSelected.length) {
      setActiveStep(studiosSelected.length === 0 ? 0 : studiosSelected.length - 1)
    }
  }, [activeStep, studiosSelected]);

  return (
    <>
      <Box
        sx={{
          margin: "0 auto",
        }}
      >
        {studiosSelected.length > 1 ? (
          <CustomTabs value={activeStep} centered>
            {studiosSelected.map((tab: any, index: number) => (
              <Tab
                key={tab.id}
                sx={{
                  width: `${100 / studiosSelected.length - 1}%`,
                }}
                label={tab.title}
                disableRipple
                onClick={() => handleChangeStep(index)}
              />
            ))}
          </CustomTabs>
        ) : null}
      </Box>
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          <form>
            <Box>
              {studiosSelected.map((studio: any, index: any) => {
                const availableServices = {
                  templates: getAvailableStudioTemplates(
                    studio.categories,
                    studio.services?.map((service: any) => service.id)
                  ),
                  ids: studio.services?.map((service: any) => service.id)
                }
                const buttonAvailability = loadedExistingServices
                  .filter((workplace) => workplace.studioId === studio.id)[0]?.skills.length
                  === availableServices.ids.length || availableServices.ids.length < 1
                return (
                  index === activeStep && (
                    <Box>
                      {(availableServices && availableServices.templates.length > 0) &&
                        <Box sx={{marginTop: '32px'}}>
                          <InfoString child = "There is not a single service added to this studio"/>
                        </Box>
                      }

                      {loadedExistingServices
                        .filter(
                          (workplace) => workplace.studioId === studio.id
                        )
                        .map((workplace) => (
                          <Box>
                            {workplace.skills.map(
                              (skill: any, skillIndex: number) => (
                                <SkillSelector
                                  key={`${skill.service}${studio.id}${skillIndex}`}
                                  index={skillIndex}
                                  studioId={studio.id}
                                  currentWorkplace={workplace}
                                  currentSkill={skill}
                                  existingServices={workplace.skills}
                                  availableServices={availableServices}
                                  onChange={handleExistingServicesChange}
                                />
                              )
                            )}
                            {buttonAvailability &&
                              <InfoString child={
                                `There is only ${availableServices.ids.length} services available for this studio`
                              }/>
                            }
                          </Box>
                        ))}
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            margin: "40px 0 80px",
                          }}
                        >
                          {studiosSelected.length > 1 &&
                          (activeStep !== 0 || activeStep + 1 === studiosSelected.length) && (
                            <SubmitButton
                              title="Back to previous studio"
                              startIcon={<ChevronLeft />}
                              onClick={handlePressPrev}
                              sx={{
                                flex: 1,
                                borderRadius: "6px",
                                marginRight: activeStep + 1 === studiosSelected.length &&
                                  buttonAvailability ? "0px" : "7px",
                                width: "100%",
                              }}
                            />
                          )}

                          {!buttonAvailability && (
                            <SubmitButton
                              endIcon={<PlusIcon fontSize="inherit" sx={{ color: "#BF295C" }}/>}
                              title="Add Skill"
                              onClick={() => handleAddServiceButton(studio.id)}
                              sx={{
                                flex: 1,
                                textAlign: "center",
                                color: "#BF295C",
                                margin: activeStep === 0 ? '0px 7px 0px 0px' :
                                  activeStep === studiosSelected.length - 1 ? '0px 0px 0px 7px' : '0px 7px',
                                width: "100%",
                                border: "1px dashed #D6D6DB",
                                backgroundColor: "white",
                                borderColor: "#B9B9B9",
                                "&:hover": {
                                  backgroundColor: "#FFFBFC !important",
                                },
                                "&:active": {
                                  backgroundColor: "#FFF8FA !important",
                                },
                              }}
                            />
                          )}

                          {studiosSelected.length > 1 && activeStep !== studiosSelected.length - 1 && (
                            <SubmitButton
                              endIcon={<ChevronRight />}
                              title="Proceed to next studio"
                              onClick={handlePressNext}
                              sx={{
                                flex: 1,
                                borderRadius: "6px",
                                marginLeft: activeStep === 0 && buttonAvailability ? "0px" : "7px",
                                width: "100%",
                              }}
                            />
                          )}
                        </Box>
                      </>
                    </Box>
                  )
                );
              })}
            </Box>
          </form>
        </Box>
      </Box>
    </>
  );
});

export {EmployeeSkillService}
